import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { ShieldCheck, ArrowRight } from 'lucide-react';

// Keep in sync with the backend LEGAL_AGREEMENT_VERSION and the mobile
// agreement copy. Bump the version to force re-acceptance after material changes.
const AGREEMENT_VERSION = '1.0.0';
const AGREEMENT_EFFECTIVE = 'Effective June 2026';
const AGREEMENT_INTRO =
  'Welcome to Brotherhood+. Before you continue, please read and accept the terms below. By checking the box and clicking \u201CI Agree & Continue,\u201D you confirm that you have read, understood, and agree to be legally bound by these terms and our Privacy Policy.';

const SECTIONS = [
  { heading: '1. Acceptance of Terms', body: 'This Agreement is a binding contract between you and Brotherhood+. It governs your access to and use of the Brotherhood+ mobile app, website, content, and related services (the \u201CServices\u201D). If you do not agree, you may not use the Services.' },
  { heading: '2. Eligibility (18+)', body: 'You must be at least 18 years old to create an account or use the Services. By continuing, you represent that you are 18 or older and that all information you provide is accurate.' },
  { heading: '3. Not Medical, Therapy, or Crisis Care', body: 'Brotherhood+ provides education, self-improvement tools, community, and AI-assisted coaching for personal growth and wellness. It is NOT therapy, counseling, medical, psychiatric, or crisis care, and it is not a substitute for a licensed professional. Nothing in the Services creates a therapist-client, doctor-patient, or professional relationship. Always seek the advice of a qualified professional for any medical or mental-health concern.' },
  { heading: '4. No Emergency Services', body: 'The Services are not designed for emergencies. If you or someone else may be in danger, or are experiencing a medical or mental-health emergency, call 911 (US) or your local emergency number immediately. In the US you can also call or text 988 (Suicide & Crisis Lifeline) any time.' },
  { heading: '5. AI Coaching Limitations', body: 'AI Coach and Support Coach are automated tools that can be inaccurate, incomplete, or inappropriate for your situation. They do not diagnose, treat, or replace professional care. Use your own judgment, and do not rely on AI responses for medical, legal, financial, or safety decisions.' },
  { heading: '6. Community Standards & Conduct', body: 'The community spaces are for support and growth. You agree not to harass, threaten, discriminate against, or share content that is illegal, hateful, or harmful. You are solely responsible for what you post. We may moderate, remove content, or suspend accounts that violate these standards, at our discretion.' },
  { heading: '7. Subscriptions & Billing', body: 'Access requires a paid subscription (Brotherhood+ Member or Brotherhood Support). Subscriptions renew automatically until canceled. On iOS, billing and renewals are managed by your Apple App Store account; on the web, by our payment processor. You can cancel any time through the applicable store or billing portal. Fees are non-refundable except where required by law.' },
  { heading: '8. Privacy & Your Data', body: 'We collect and process your information as described in our Privacy Policy. Your check-ins, journal entries, and coaching conversations are treated as private and are used to personalize your experience. We do not sell your personal information. You control your data and may request deletion of your account at any time.' },
  { heading: '9. Assumption of Risk & Limitation of Liability', body: 'You use the Services voluntarily and at your own risk. To the fullest extent permitted by law, Brotherhood+ and its affiliates are not liable for any indirect, incidental, or consequential damages, or for any decision you make based on the Services. Our total liability is limited to the amount you paid us in the 12 months before the claim. The Services are provided \u201Cas is\u201D without warranties of any kind.' },
  { heading: '10. Governing Law & Changes', body: 'This Agreement is governed by the laws of the United States and your state of residence, without regard to conflict-of-law rules. We may update these terms from time to time; continued use after an update means you accept the revised terms. You will be asked to re-accept when we make material changes.' },
];

const CHECKBOX_LABEL =
  'I have read and agree to the Terms of Service and Privacy Policy, and I understand that Brotherhood+ is not a substitute for therapy, medical care, or emergency services.';

/**
 * AgreementGate — blocking, one-time user-agreement acknowledgement shown on
 * first login. Requires an explicit checkbox before continuing, then persists
 * acceptance to the backend with a timestamp. Mirrors the mobile AgreementGate.
 */
export default function AgreementGate() {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get('/me/agreement-status')
      .then(({ data }) => { if (!data?.accepted) setOpen(true); })
      .catch(() => { /* ignore — don't trap the user on a network blip */ });
  }, []);

  if (!open) return null;

  const accept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try { await http.post('/me/accept-agreement', { version: AGREEMENT_VERSION }); } catch { /* best-effort */ }
    setSubmitting(false);
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex flex-col"
      style={{ backgroundColor: '#F9F8F6' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-gate-title"
      data-testid="agreement-gate"
    >
      {/* Header */}
      <div className="px-5 lg:px-8 pt-6 pb-4 border-b border-navy/10 shrink-0">
        <div className="max-w-2xl mx-auto w-full">
          <div className="w-11 h-11 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-3">
            <ShieldCheck size={20} className="text-gold" strokeWidth={2}/>
          </div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold font-semibold">Before you begin</p>
          <h2 id="agreement-gate-title" className="font-heading text-2xl lg:text-3xl text-navy mt-1">User Agreement</h2>
          <p className="text-sm text-navy/50 mt-1">{AGREEMENT_EFFECTIVE}</p>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 lg:px-8 py-6">
        <div className="max-w-2xl mx-auto w-full">
          <p className="text-sm text-navy/75 leading-relaxed mb-6">{AGREEMENT_INTRO}</p>
          {SECTIONS.map((s) => (
            <div key={s.heading} className="mb-5">
              <h3 className="text-base font-bold text-navy mb-1">{s.heading}</h3>
              <p className="text-sm text-navy/65 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 lg:px-8 py-4 border-t border-navy/10 bg-white shrink-0">
        <div className="max-w-2xl mx-auto w-full">
          <button
            type="button"
            onClick={() => setChecked((v) => !v)}
            data-testid="agreement-checkbox"
            className="flex items-start gap-3 text-left w-full mb-4"
          >
            <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-smooth ${checked ? 'bg-navy border-navy' : 'border-navy'}`}>
              {checked && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </span>
            <span className="text-xs text-navy/80 leading-snug">{CHECKBOX_LABEL}</span>
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={!checked || submitting}
            data-testid="agreement-accept"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white font-bold text-sm py-3.5 hover:brightness-110 transition-smooth disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving\u2026' : 'I Agree & Continue'}
            {!submitting && <ArrowRight size={16}/>}
          </button>
        </div>
      </div>
    </div>
  );
}
